(() => {
  if (window.__formeHeaderLoaded) return;
  window.__formeHeaderLoaded = true;

  class FormeHeader {
    constructor(root) {
      this.root = root;
      this.controller = new AbortController();
      this.searchController = null;
      this.searchTimer = 0;
      this.scrollFrame = 0;
      this.openDropdown = null;
      this.drawerOpener = null;
      this.searchIndex = -1;
      this.sectionWrapper = root.closest('.shopify-section');
      this.stickyOrigin = root.getBoundingClientRect().top + window.scrollY;
      root.dataset.headerInitialized = 'true';
      this.bind();
      this.measure();
      this.updateSticky();
    }

    bind() {
      const signal = this.controller.signal;
      this.root.querySelectorAll('[data-mobile-menu-trigger]').forEach((button) => button.addEventListener('click', () => this.toggleDrawer(button), { signal }));
      this.root.querySelectorAll('[data-mobile-menu-close]').forEach((button) => button.addEventListener('click', () => this.closeDrawer(true), { signal }));
      this.root.querySelectorAll('[data-mobile-submenu-open]').forEach((button) => button.addEventListener('click', () => this.openSubmenu(button), { signal }));
      this.root.querySelectorAll('[data-mobile-submenu-back]').forEach((button) => button.addEventListener('click', () => this.closeSubmenu(button.closest('[data-mobile-submenu]'), true), { signal }));
      this.root.querySelectorAll('[data-desktop-menu-toggle]').forEach((button) => {
        button.addEventListener('click', () => this.toggleDropdown(button), { signal });
        const item = button.closest('[data-desktop-menu-item]');
        item.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          window.clearTimeout(this.hoverTimer);
          this.hoverTimer = window.setTimeout(() => this.openDesktopDropdown(button), 120);
        }, { signal });
        item.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'mouse') return;
          window.clearTimeout(this.hoverTimer);
          this.hoverTimer = window.setTimeout(() => {
            if (!item.matches(':focus-within')) this.closeDesktopDropdown(button, false);
          }, 200);
        }, { signal });
      });
      document.addEventListener('pointerdown', (event) => {
        if (this.openDropdown && !event.composedPath().includes(this.openDropdown.closest('[data-desktop-menu-item]'))) this.closeAllDropdowns(false);
      }, { signal });
      document.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      window.addEventListener('scroll', () => this.queueStickyUpdate(), { passive: true, signal });
      window.addEventListener('resize', () => {
        this.measure();
        if (window.innerWidth >= 1024) {
          if (this.root.dataset.headerState !== 'compact') this.closeDrawer(false);
        } else this.closeAllDropdowns(false);
      }, { passive: true, signal });

      this.searchInput = this.root.querySelector('[data-search-input]');
      this.searchType = this.root.querySelector('[data-search-type]');
      this.searchPanel = this.root.querySelector('[data-predictive-search]');
      if (this.searchInput && this.searchPanel) {
        this.searchInput.addEventListener('focus', () => this.openSearch(), { signal });
        this.searchInput.addEventListener('input', () => this.queueSearch(), { signal });
        this.searchInput.addEventListener('keydown', (event) => this.onSearchKeydown(event), { signal });
        this.searchType?.addEventListener('change', () => {
          if (this.searchInput.value.trim().length >= 2) this.queueSearch();
        }, { signal });
        this.root.querySelector('[data-search-clear]')?.addEventListener('click', () => this.clearSearch(), { signal });
        this.root.querySelector('[data-search-close]')?.addEventListener('click', () => this.closeSearch(true), { signal });
      }
      document.addEventListener('cart:refresh', () => this.refreshCartCount(), { signal });
    }

    measure() {
      const rect = this.root.getBoundingClientRect();
      const topHeight = this.root.querySelector('.site-header__top')?.offsetHeight || 0;
      const bottomHeight = this.root.querySelector('.site-header__bottom')?.offsetHeight || 0;
      const fullHeight = topHeight + bottomHeight;
      if (this.sectionWrapper && fullHeight > 0) {
        this.sectionWrapper.style.minHeight = `${fullHeight}px`;
        this.stickyOrigin = this.sectionWrapper.getBoundingClientRect().top + window.scrollY;
      }
      this.root.style.setProperty('--visible-header-height', `${rect.height}px`);
      this.root.style.setProperty('--visible-header-bottom', `${Math.max(0, rect.bottom)}px`);
    }

    queueStickyUpdate() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = 0;
        this.updateSticky();
      });
    }

    updateSticky() {
      const editorMode = window.Shopify?.designMode === true;
      const pinned = !editorMode && this.root.dataset.sticky === 'true' && window.scrollY >= this.stickyOrigin;
      const compact = pinned && window.innerWidth >= 1024 && this.root.dataset.collapseNav === 'true';
      this.root.dataset.headerState = compact ? 'compact' : pinned ? 'pinned' : 'top';
      if (compact) this.closeAllDropdowns(false);
      this.measure();
    }

    toggleDropdown(button) {
      if (button.getAttribute('aria-expanded') === 'true') this.closeDesktopDropdown(button, true);
      else this.openDesktopDropdown(button);
    }

    openDesktopDropdown(button) {
      if (window.innerWidth < 1024) return;
      this.closeAllDropdowns(false, button);
      const panel = document.getElementById(button.getAttribute('aria-controls'));
      if (!panel) return;
      button.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      this.openDropdown = button;
    }

    closeDesktopDropdown(button, returnFocus) {
      if (!button) return;
      button.setAttribute('aria-expanded', 'false');
      const panel = document.getElementById(button.getAttribute('aria-controls'));
      if (panel) window.setTimeout(() => { if (button.getAttribute('aria-expanded') === 'false') panel.hidden = true; }, 500);
      if (this.openDropdown === button) this.openDropdown = null;
      if (returnFocus) button.focus();
    }

    closeAllDropdowns(returnFocus = false, except = null) {
      this.root.querySelectorAll('[data-desktop-menu-toggle][aria-expanded="true"]').forEach((button) => {
        if (button !== except) this.closeDesktopDropdown(button, returnFocus);
      });
    }

    toggleDrawer(opener) {
      if (this.root.dataset.menuOpen === 'true') this.closeDrawer(true);
      else this.openDrawer(opener);
    }

    openDrawer(opener) {
      const drawer = this.root.querySelector('[data-mobile-menu]');
      if (!drawer) return;
      this.closeSearch(false);
      this.drawerOpener = opener;
      this.root.dataset.menuOpen = 'true';
      this.root.querySelectorAll('[data-mobile-menu-trigger]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('header-scroll-locked');
      const defaultSubmenuButton = drawer.querySelector('[data-mobile-submenu-default]');
      if (defaultSubmenuButton) this.openSubmenu(defaultSubmenuButton, false);
      const drawerScroll = drawer.querySelector('.site-mobile-menu__scroll');
      if (drawerScroll) drawerScroll.scrollLeft = 0;
      const focusScope = drawer.querySelector('[data-mobile-submenu][aria-hidden="false"]') || drawer;
      focusScope.querySelector('a, button:not([tabindex="-1"])')?.focus({ preventScroll: true });
    }

    closeDrawer(returnFocus) {
      const drawer = this.root.querySelector('[data-mobile-menu]');
      if (!drawer) return;
      this.root.dataset.menuOpen = 'false';
      this.root.querySelectorAll('[data-mobile-menu-trigger]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      this.root.querySelectorAll('[data-mobile-submenu]').forEach((submenu) => this.closeSubmenu(submenu, false));
      if (!this.root.classList.contains('is-search-open')) document.documentElement.classList.remove('header-scroll-locked');
      if (returnFocus && this.drawerOpener?.isConnected) this.drawerOpener.focus();
    }

    openSubmenu(button, moveFocus = true) {
      const submenu = document.getElementById(button.getAttribute('aria-controls'));
      if (!submenu) return;
      button.setAttribute('aria-expanded', 'true');
      submenu.setAttribute('aria-hidden', 'false');
      if (moveFocus) window.setTimeout(() => submenu.querySelector('[data-mobile-submenu-back]')?.focus({ preventScroll: true }), 30);
    }

    closeSubmenu(submenu, returnFocus) {
      if (!submenu) return;
      const button = this.root.querySelector(`[aria-controls="${CSS.escape(submenu.id)}"]`);
      button?.setAttribute('aria-expanded', 'false');
      submenu.setAttribute('aria-hidden', 'true');
      if (returnFocus) button?.focus();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        const activeSubmenu = this.root.querySelector('[data-mobile-submenu][aria-hidden="false"]');
        if (activeSubmenu) this.closeSubmenu(activeSubmenu, true);
        else if (this.root.dataset.menuOpen === 'true') this.closeDrawer(true);
        else if (this.root.classList.contains('is-search-open')) this.closeSearch(true);
        else if (this.openDropdown) this.closeDesktopDropdown(this.openDropdown, true);
      }
      if (event.key === 'Tab' && this.root.dataset.menuOpen === 'true') this.trapFocus(event, this.root.querySelector('[data-mobile-menu]'));
    }

    trapFocus(event, container) {
      const focusable = [...container.querySelectorAll('a[href], button:not([disabled]):not([tabindex="-1"]), input:not([disabled]), select:not([disabled])')].filter((item) => item.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }

    openSearch() {
      if (this.root.dataset.searchEnabled !== 'true' || !this.searchPanel) return;
      this.closeDrawer(false);
      this.closeAllDropdowns(false);
      this.root.classList.add('is-search-open');
      this.searchPanel.hidden = false;
      this.searchInput.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('header-scroll-locked');
      this.root.querySelector('[data-search-clear]').hidden = this.searchInput.value === '';
      this.measure();
    }

    closeSearch(returnFocus) {
      if (!this.searchPanel) return;
      this.root.classList.remove('is-search-open');
      this.searchPanel.hidden = true;
      this.searchInput?.setAttribute('aria-expanded', 'false');
      if (this.root.dataset.menuOpen !== 'true') document.documentElement.classList.remove('header-scroll-locked');
      if (returnFocus) this.searchInput?.focus();
    }

    clearSearch() {
      this.searchInput.value = '';
      this.root.querySelector('[data-search-clear]').hidden = true;
      this.renderSearch([], this.root.querySelector('[data-search-status]')?.dataset.prompt || '');
      this.searchInput.focus();
    }

    queueSearch() {
      window.clearTimeout(this.searchTimer);
      const clear = this.root.querySelector('[data-search-clear]');
      if (clear) clear.hidden = this.searchInput.value === '';
      this.searchTimer = window.setTimeout(() => this.search(this.searchInput.value.trim()), 250);
    }

    async search(query) {
      const status = this.root.querySelector('[data-search-status]');
      if (query.length < 2) {
        this.renderSearch([], query ? this.datasetMessage('messageNoResults') : '');
        return;
      }
      this.searchController?.abort();
      this.searchController = new AbortController();
      if (status) status.textContent = this.datasetMessage('messageSearching');
      const url = new URL(this.root.dataset.searchUrl, window.location.origin);
      url.pathname = `${url.pathname}.json`;
      const categoryTag = this.searchType?.value || '';
      const scopedQuery = categoryTag ? `${query} tag:${categoryTag}` : query;
      url.searchParams.set('q', scopedQuery);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      try {
        const response = await fetch(url, { signal: this.searchController.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`Search failed: ${response.status}`);
        const data = await response.json();
        const products = data.resources?.results?.products || [];
        const message = products.length ? this.datasetMessage('messageResults').replace('__COUNT__', products.length) : this.datasetMessage('messageNoResults');
        this.renderSearch(products, message);
      } catch (error) {
        if (error.name !== 'AbortError') this.renderSearch([], this.datasetMessage('messageSearchError'));
      }
    }

    datasetMessage(key) { return this.root.dataset[key] || ''; }

    renderSearch(products, message) {
      const results = this.root.querySelector('[data-search-results]');
      const status = this.root.querySelector('[data-search-status]');
      if (!results) return;
      results.replaceChildren();
      if (status) status.textContent = message;
      if (!products.length) {
        if (message) { const empty = document.createElement('p'); empty.className = 'predictive-search__empty'; empty.textContent = message; results.append(empty); }
        return;
      }
      const list = document.createElement('ul');
      list.className = 'predictive-search__list';
      list.setAttribute('role', 'listbox');
      products.forEach((product, index) => {
        const item = document.createElement('li');
        item.setAttribute('role', 'option');
        const link = document.createElement('a');
        link.href = product.url;
        link.dataset.searchOption = String(index);
        if (product.featured_image?.url) { const image = document.createElement('img'); image.src = product.featured_image.url; image.alt = ''; image.loading = 'lazy'; link.append(image); }
        const title = document.createElement('span'); title.textContent = product.title; link.append(title); item.append(link); list.append(item);
      });
      results.append(list);
      this.searchIndex = -1;
    }

    onSearchKeydown(event) {
      if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;
      const options = [...this.root.querySelectorAll('[data-search-option]')];
      if (!options.length) return;
      if (event.key === 'Enter' && this.searchIndex >= 0) { event.preventDefault(); options[this.searchIndex].click(); return; }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.searchIndex = event.key === 'ArrowDown' ? (this.searchIndex + 1) % options.length : (this.searchIndex - 1 + options.length) % options.length;
        options[this.searchIndex].focus();
        this.searchInput.setAttribute('aria-activedescendant', `HeaderSearchOption-${this.searchIndex}`);
      }
    }

    async refreshCartCount() {
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const cart = await response.json();
        const count = this.root.querySelector('[data-cart-count]');
        if (count) { count.textContent = cart.item_count; count.hidden = cart.item_count === 0; }
      } catch (error) {}
    }

    destroy() {
      this.controller.abort();
      this.searchController?.abort();
      window.clearTimeout(this.searchTimer);
      window.clearTimeout(this.hoverTimer);
      cancelAnimationFrame(this.scrollFrame);
      document.documentElement.classList.remove('header-scroll-locked');
      if (this.sectionWrapper) this.sectionWrapper.style.minHeight = '';
      delete this.root.dataset.headerInitialized;
    }
  }

  const instances = new Map();
  const initialize = (scope = document) => scope.querySelectorAll('[data-site-header]').forEach((root) => {
    if (root.dataset.headerInitialized === 'true') return;
    instances.set(root.id, new FormeHeader(root));
  });
  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[data-site-header]');
    if (!root) return;
    instances.get(root.id)?.destroy();
    instances.delete(root.id);
  });
})();
